import React, { useEffect, useState } from "react";
import { withLazy } from "./components/LazyRoute";
import { I18nextProvider, useTranslation } from "react-i18next";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { connect, useDispatch } from 'react-redux';
import { useComplianceCountry } from "./hooks/useComplianceCountry";
// import "moment/min/locales";
import CurrentStatus from "./components/currentStatus";
import AIAssistant from "./components/ai-chat";
import OtpToast from "./components/notifications/OtpToast";
import NotificationPopup from "./components/notifications/NotificationPopup";
import moment from "moment";
import constants from "./assets/constants";
import PrivateRoute from "./PrivateRoute";
import Callback from "./Callback";
import LogoutCallback from "./LogoutCallback";
import SilentRenewCallback from "./SilentRenewCallback";

// New layout system (Tailwind-based)
import { MasterLayout } from "./components/layout";
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
import { ShiftSwapEnabledRoute } from "./components/ShiftSwapEnabledRoute";
import { ProfileAccessGuard } from "./components/ProfileAccessGuard";
import OnboardingGate from "./components/OnboardingGate";
import ErrorBoundary from "./components/ErrorBoundary";

// Roles that can access manager-level routes
const MANAGER_ROLES = ['Manager', 'Hr', 'Chief'];
// Roles that can access operator-level routes (system-wide settings)
const OPERATOR_ROLES = ['Operator'];
// Roles that can access settings routes (HR and Admin). Operator is a subscription/platform-only
// actor (RBAC model) and must never reach company settings/config — it uses operator-specific routes.
const SETTINGS_ROLES = ['Hr', 'Admin'];
// Roles that can access HR-only routes
const HR_ONLY_ROLES = ['Hr'];
// Roles that can access HR + Operator routes (e.g. Compliance Config)
const HR_AND_OPERATOR_ROLES = ['Hr', 'Operator'];
// HR + Admin (company-wide management)
const HR_AND_ADMIN_ROLES = ['Hr', 'Admin'];
// Core HR module audience — managers (incl. team-level) + Admin.
// Mirrors the `people` module roles in navigationModules.ts ([...MANAGER, 'Admin']).
const CORE_HR_ROLES = ['Manager', 'Hr', 'Chief', 'Admin'];

import CompanyDashboard from "./views/CompanyDashboard";
const PendingActions = withLazy(() => import("./views/PendingActions"));
const AIConsultant = withLazy(() => import("./views/AIConsultant"));
const AIConsultantHistory = withLazy(() => import("./views/AIConsultant/History"));
import NotFound from "./views/General/error/404";
import Notifications from "./views/General/notifications";
import Organizations from "./views/Settings/Organizations";
import AddOrganization from "./views/Settings/Organizations/AddOrganization";
import Companies from "./views/Settings/Companies";
import AddUser from "./views/EmployeesCycle/General/add-user";
import OnDemandNotification from "./views/EmployeesCycle/General/onDemandNotification";
import SignupFree from "./views/General/Signup/SignupFree";
import StartTrial from "./views/General/Signup/StartTrial";
import PlatformTermsIndex from "./views/Settings/PlatformTerms/PlatformTermsIndex";
import PrivacyPolicy from "./views/General/privacyPolicy";
import Widget from "./views/General/widget";

const Attendance = withLazy(() => import("./views/Attendance/attendance"));
import AttendanceOverview from "./views/Attendance/AttendanceOverview";
import AttendanceCorrectionRequests from "./views/Attendance/CorrectionRequests";
// LeavesAndHolidays removed – legacy page deleted, routes redirect to /my-leaves
import Reports from "./views/Attendance/Reports";
const Maps = withLazy(() => import("./views/Attendance/map"));
import Dashboard from "./views/Attendance/Dashboard";
import WorkSchedules from "./views/Attendance/WorkSchedules";
import RecurringSchedules from "./views/Attendance/WorkSchedules/RecurringSchedules";
import TeamAttendancePage from "./views/Attendance/team-attendance-page";

import ClockingsReport from "./views/Attendance/Reports/tabs/Clockings";
const ShiftsReport = withLazy(() => import("./views/Attendance/Reports/tabs/Shifts"));
const OvertimeReport = withLazy(() => import("./views/Attendance/Reports/tabs/Overtime"));
const OutOfBoundsReport = withLazy(() => import("./views/Attendance/Reports/tabs/OutOfBounds"));
import WorkSessionsReport from "./views/Attendance/Reports/tabs/WorkSessions";

import UsersStatusesReportExport from "./components/reportsDownload/UsersStatuses";
const ClockingsReportExport = withLazy(() => import("./components/reportsDownload/Clockings"));
const ShiftsReportExport = withLazy(() => import("./components/reportsDownload/Shifts"));
const OvertimeReportExport = withLazy(() => import("./components/reportsDownload/Overtime"));

import EmployeesGeneral from "./views/EmployeesCycle/General";
import SetUp from "./views/EmployeesCycle/SetUp";
import EmployeesGrouping from "./views/EmployeesCycle/EmployeeGrouping"
import SkillsOverview from "./views/EmployeesCycle/SkillsOverview"
import OrganizationalStructure from "./views/EmployeesCycle/OrganizationalStructure"

// Tournament Management imports
import TournamentList from "./views/TournamentManagement/tournament-list";
import TournamentMatches from "./views/TournamentManagement/tournament-matches";
import RefereeAssignmentCenter from "./views/TournamentManagement/referee-assignment-center";
import WorkPointSettings from "./views/TournamentManagement/work-point-settings";
import MyAssignments from "./views/TournamentManagement/my-assignments";
import Compensation from "./views/TournamentManagement/compensation";
import RefereeWorkload from "./views/TournamentManagement/referee-workload";

import DependentsDirectory from "./views/DependentsDirectory";
import TeamRoleManagement from "./views/TeamRoleManagement";

// Coming Soon preview pages (Plans 08-14)
import EmployeeDocumentCenter from "./views/ComingSoon/EmployeeDocumentCenter";
import EmploymentLifecycle from "./views/ComingSoon/EmploymentLifecycle";
import DepartmentsLocations from "./views/DepartmentsLocations";
import JobTitlesGrades from "./views/ComingSoon/JobTitlesGrades";
import CompanyFilesPolicies from "./views/ComingSoon/CompanyFilesPolicies";
import ProfileAuthorization from "./views/ComingSoon/ProfileAuthorization";
import DocumentTemplateManager from "./views/CoreHR/document-template-manager";
// TeamRoleManagement now imported from ./views/TeamRoleManagement (non-ComingSoon)
import HRProcessAutomation from "./views/ComingSoon/HRProcessAutomation";
import CoreHRReports from "./views/Reports/CoreHRReports";
import AttendanceDashboard from "./views/ComingSoon/AttendanceDashboard";
import DutyRosterManagement from "./views/ComingSoon/DutyRosterManagement";
import RefereeManagement from "./views/TournamentManagement/referee-management";
import PayrollRunWizard from "./views/ComingSoon/PayrollRunWizard";
import PayrollDocuments from "./views/ComingSoon/PayrollDocuments";
import PayrollReports from "./views/Payroll/PayrollReports";
import EOSCenter from "./views/ComingSoon/EOSCenter";
import EmployeeDevelopmentHub from "./views/ComingSoon/EmployeeDevelopmentHub";
import DevelopmentObjectivesCreator from "./views/ComingSoon/DevelopmentObjectivesCreator";
import DevelopmentObjectiveDetail from "./views/ComingSoon/DevelopmentObjectiveDetail";
import TeamDevelopmentDashboard from "./views/ComingSoon/TeamDevelopmentDashboard";
import CheckInsHub from "./views/PerformanceManagement/CheckInsHub";
import PerformanceSnapshot from "./views/PerformanceSnapshot";
import BehaviorValues from "./views/ComingSoon/BehaviorValues";
import CoreHRSettings from "./views/ComingSoon/CoreHRSettings";
import TaskBoard from "./views/ComingSoon/TaskBoard";
import ProjectsList from "./views/ComingSoon/ProjectsList";
import MarketplaceHub from "./views/ComingSoon/MarketplaceHub";
import BulkUploadPage from "./views/General/BulkUpload";
import AssetsPage from "./views/General/Assets";
const HRENPSAnalyticsDashboard = withLazy(() => import("./views/PerformanceManagement/ENPSAnalytics"));
import MyLeaves from "./views/Attendance/MyLeaves";
import LeaveApprovals from "./views/Attendance/LeaveApprovals";
import ShiftSwaps from "./views/Attendance/ShiftSwaps";
import DelegationManagement from "./views/Attendance/DelegationManagement";
import TeamCalendar from "./views/Attendance/TeamCalendar";
import ReturnToWork from "./views/Attendance/ReturnToWork";
import CompOffCredits from "./views/Attendance/CompOffCredits";
import ShortLeave from "./views/Attendance/ShortLeave";
import Encashment from "./views/Attendance/Encashment";
import RecurringLeave from "./views/Attendance/RecurringLeave";
// RestrictedHolidays merged into HolidaysLocationsTab (Leave Settings → Holidays tab)
import StatutoryLeaveTemplates from "./views/Attendance/StatutoryLeaveTemplates";
import YearEndProcessing from "./views/Attendance/YearEndProcessing";
const LeaveStatements = withLazy(() => import("./views/Attendance/LeaveStatements"));
const LeaveBalances = withLazy(() => import("./views/Attendance/LeaveBalances"));
import AbsenceAnalytics from "./views/Attendance/AbsenceAnalytics";
import LeaveSettings from "./views/Settings/Attendance/LeaveSettings";

// Public Profile pages
import ProfileSettings from "./views/ProfileSettings";
import ProfileConfigurationCenter from "./views/ProfileConfigurationCenter";
import HRAdminPublicProfileSettings from "./views/HRAdminPublicProfileSettings";
import PublicProfileDisplay from "./views/PublicProfileDisplay";

// Performance Management imports
import Recognition from "./views/PerformanceManagement/Recognition"
import Reflections from "./views/PerformanceManagement/Reflections"
import Goals from "./views/PerformanceManagement/Goals"
import ESPN from "./views/PerformanceManagement/ESPN"
import TeamHub from "./views/PerformanceManagement/TeamHub"
const InsightsDashboard = withLazy(() => import("./views/PerformanceManagement/InsightsDashboard"))
import ImpactDashboard from "./views/PerformanceManagement/ImpactDashboard"
import MissionStatements from "./views/PerformanceManagement/MissionStatements"
import AIImpactDashboard from "./views/PerformanceManagement/AIImpactDashboard"
import TeamReflections from "./views/PerformanceManagement/ManagerResponse"
const Mentorship = withLazy(() => import("./views/PerformanceManagement/Mentorship"));
import UserPayroll from "./views/Payroll/TablePayroll";
const DashboardPayroll = withLazy(() => import("./views/Payroll/DashboardPayroll"));
import Claims from "./views/Payroll/Claims"
import ClaimDetails from "./views/Payroll/Claims/ClaimDetails"
import MyClaims from "./views/Payroll/MyClaims"
import Payslips from "./views/Payroll/Payslips";
import ComplianceCenter from "./views/Payroll/ComplianceCenter";
import WpsCenter from "./views/Payroll/WpsCenter";
import PensionCenter from "./views/Payroll/PensionCenter";

import Currency from "./views/Settings/General/currency";
import GeneralDetails from "./views/Settings/General/generalDetails";
import BranchesAndDepartments from "./views/Settings/General/branchesAndDepartments";
import Entities from "./views/Settings/General/entities";
import AttachmentTypes from "./views/Settings/General/attachmentTypes";
import DateFormatConfiguration from "./views/Settings/General/DateFormatConfiguration";

import GrossPaysConfig from "./views/Settings/Payroll/GrossPaysSettings";
import ClaimsSettings from "./views/Settings/Payroll/ClaimsSettings";
import CompanyVariablePayTypesSettings from "./views/Settings/Payroll/CompanyVariablePayTypesSettings";
import CompanyBenefitTypesSettings from "./views/Settings/Payroll/BenefitsSettings";
import EndOfServiceSettings from "./views/Settings/Payroll/EndOfService";
import PayslipsSettings from "./views/Settings/Payroll/Payslips";
import ComplianceSettings from "./views/Settings/Payroll/ComplianceSettings";

import TermsAndPoliciesSetup from "./views/Settings/Company/TermsAndPoliciesSetup";
import MyOnboardingPage from "./views/Onboarding/MyOnboardingPage";
import TermsAcceptanceView from "./views/Onboarding/TermsAcceptanceView";
import LifecycleSettings from "./views/Settings/Employees/LifecycleSettings";
import AssetManagementSettings from "./views/Settings/CoreHR/AssetManagement";

import RecognitionPrivacySettings from "./views/Settings/PreformanceManagement/RecognitionPrivacy";
import RecognitionValues from "./views/Settings/PreformanceManagement/RecognitionValues";

import Personal from "./views/UserProfile/Personal"
import Payroll from "./views/UserProfile/UserPayroll/ActivePayrollMonth";
import Adjustments from "./views/UserProfile/UserPayroll/Adjustments";
// import WorkExpenses from "./components/UserProfile/UserPayroll/WorkExpenses";
import PayHistory from "./views/UserProfile/UserPayroll/PayHistory";
import SalaryConfiguration from "./views/UserProfile/UserPayroll/SalaryConfiguration";
import EndOfService from "./views/UserProfile/UserPayroll/EndOfService";
import FinalSettlement from "./views/UserProfile/UserPayroll/FinalSettlement";
import AttendanceRules from "./views/UserProfile/Attendance/Rules";
import AttendanceMap from "./views/UserProfile/Attendance/WorkplaceMap";
import UserProfileLeaves from "./views/UserProfile/Leaves";
import VariablePay from "./views/UserProfile/UserPayroll/VariablePay";
import UserBenefits from "./views/UserProfile/UserPayroll/Benefits";
import Skills from "./views/UserProfile/Skills";
import Documents from "./views/UserProfile/Documents";
import ProfileAssets from "./views/UserProfile/Assets";
import ProfileBusinessRoles from "./views/UserProfile/BusinessRoles";
import ProfileDependents from "./views/UserProfile/Dependents";
import UserProfilePayslips from "./views/UserProfile/UserPayroll/Payslips";

import AddEditCompany from "./views/Settings/Companies/AddEditCompany";
import SubscriptionsIndex from "./views/Settings/Subscriptions/SubscriptionsIndex";
const BatchAddingUsers = withLazy(() => import("./views/Settings/Companies/BatchAddingUsers"));
const WelcomeEmails = withLazy(() => import("./views/EmployeesCycle/General/WelcomeEmails"));
import CompanyUsers from "./views/Settings/Companies/CompanyUsers";
import OperatorCompanyRules from "./views/Settings/Companies/OperatorCompanyRules";
import AppVersionIndex from "./views/Settings/AppVersion/AppVersionIndex";
import CompanyHolidays from "./views/Settings/Attendance/companyHolidays";
import WorkplaceMap from "./views/Settings/Attendance/workplaceMap";
import Weekschedule from "./views/Settings/Attendance/workscheduleconfig";
import FaceRecognitionSettings from "./views/Settings/Attendance/FaceRecognitionSettings";
import WialonSettings from "./views/Settings/Attendance/WialonSettings";
import FileUploadSettings from "./views/Settings/General/FileUploadSettings";
import OcrSettings from "./views/Settings/General/OcrSettings";
import BlobMigration from "./views/Settings/BlobMigration";
import Projects from "./views/Settings/ProjectManagement/Projects";
import SystemEmails from "./views/Settings/General/systemEmails";
import Seeding from "./views/Settings/Development/Seeding";
import AISettings from "./views/Settings/AI/AISettings";
import ModuleConfig from "./views/Settings/ModuleConfig";
import UserNotificationPreferences from "./views/Settings/Notifications/UserNotificationPreferences";
import AdminNotificationManagement from "./views/Settings/Notifications/AdminNotificationManagement";
import Unsubscribe from "./views/General/unsubscribe";
import PayrollPayslipsSettings from "./views/Settings/Payroll/Payslips";

// Per-module brochures (Phase 6). The router switches on `:moduleSlug` and
// renders the matching brochure component, falling back to a "coming soon"
// card for slugs that haven't shipped their dedicated page yet.
import BrochureRouter from "./views/Brochures/BrochureRouter";

// Recruitment module (UI-only, ported from mowazaf-rocketnew)
const RecruitmentDashboard = withLazy(() => import("./views/Recruitment/recruitment-dashboard"));
const RecruitmentSettings = withLazy(() => import("./views/Recruitment/recruitment-settings"));
const JobOpeningsManagement = withLazy(() => import("./views/Recruitment/job-openings-management"));
const CandidatesManagementCenter = withLazy(() => import("./views/Recruitment/candidates-management-center"));
const HiringPipeline = withLazy(() => import("./views/Recruitment/hiring-pipeline"));
const InterviewsManagement = withLazy(() => import("./views/Recruitment/interviews-management"));
const OffersManagement = withLazy(() => import("./views/Recruitment/offers-management"));

interface IProps {
  isLoggedIn: boolean;
  fullName: string;
  role: string;
  locale: string;
  companyName: string;
  companyLogo: string | null;
  companyLocales: string[];
  companyPrimaryColor: string | null;
  currentStatus: string | null;
  usercurrentCustomStatusText: string | null;
  prevUrl: string;
  userId: string;
  imageUrl: string | null;
  anyUserWithVariableScheduleRule: boolean;
  token: string;
  isManager: boolean;
  rtl: boolean;
}

const _App = (props: IProps) => {
  const { t, i18n } = useTranslation();
  const [availabilityStatusDialogOpen, setAvailabilityStatusDialogOpen] = useState(false);
  const complianceCountry = useComplianceCountry();
  const {
    isLoggedIn,
    locale,
    companyPrimaryColor,
    rtl
  } = props;

  // Keep CSS variables in sync with company primary color without overriding pre-set var on initial mount
  useEffect(() => {
    import('./utils').then(({ generatePalette, setCSSColorVariables }) => {
      const next = companyPrimaryColor ?? '#304EA1';
      try {
        const currentVar = typeof document !== 'undefined'
          ? getComputedStyle(document.documentElement).getPropertyValue('--mowazaf-primary').trim()
          : '';
        // If initial Redux default is present but CSS var is already set (from persisted color), do not override
        const isInitialDefault = (companyPrimaryColor || '').toLowerCase() === '#304EA1';
        if (isInitialDefault && currentVar) return;
        if (currentVar && currentVar.toLowerCase() === (next || '').toLowerCase()) return;
      } catch { }
      const palette = generatePalette(next);
      setCSSColorVariables(palette);
    }).catch(() => { });
  }, [companyPrimaryColor]);

  const dispatch = useDispatch();

  // Keep i18n language in sync with persisted Redux locale (after rehydration and on changes)
  useEffect(() => {
    console.log("Current locale:", locale);
    if (!locale) return;
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
    try {
      // Update moment locale as well
      moment.locale(locale);
      if (constants.rtlLangs.indexOf(locale) > -1 && locale === 'ar') {
        // Ensure Arabic numerals formatting stays consistent if needed
        moment.defineLocale('ar-arNum', {
          parentLocale: 'ar',
          preparse: function (str: string) { return str; },
          postformat: function (str: string) { return str; }
        });
        moment.locale('ar-arNum');
      }
    } catch { }
  }, [locale, i18n]);

  // Keep HTML document direction in sync with the active i18n language.
  // The Redux `rtl` flag only exists for authenticated users; anonymous visitors
  // hitting /p/{slug} and toggling the in-page language switcher would otherwise
  // get Arabic strings with an LTR layout. Listening to i18n itself covers both.
  useEffect(() => {
    const applyDir = (lang: string | undefined) => {
      const base = (lang || 'en').toLowerCase().split('-')[0];
      const dir = constants.rtlLangs.indexOf(base) > -1 ? 'rtl' : 'ltr';
      if (typeof document === 'undefined') return;
      if (document.documentElement.getAttribute('dir') !== dir) {
        document.documentElement.setAttribute('dir', dir);
      }
      // Keep <html lang> aligned with the active language too, not just dir.
      // The static shell ships lang="en" (and the SSR /p/{slug} handler rewrites
      // it server-side); without this, a locked-language public profile (ar/fr)
      // — or any client language switch — would keep announcing "en" to screen
      // readers and crawlers even though the content and dir are Arabic/French.
      if (base && document.documentElement.getAttribute('lang') !== base) {
        document.documentElement.setAttribute('lang', base);
      }
      if (document.body && document.body.getAttribute('dir') !== dir) {
        document.body.setAttribute('dir', dir);
      }
    };

    applyDir(i18n.language);
    const onLanguageChanged = (lng: string) => applyDir(lng);
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n, rtl]);

  const closeAvailabilityStatusDialog = () => {
    setAvailabilityStatusDialogOpen(false);
  };

  return (
    <>
          <I18nextProvider i18n={i18n}>
            {/* Global dialogs that can appear anywhere */}
            <CurrentStatus
              semibold={false}
              asDialog={true}
              dialogIsOpen={availabilityStatusDialogOpen}
              closeAvailabilityStatusDialog={closeAvailabilityStatusDialog}
            />

            <div className="h-screen flex flex-col overflow-hidden">
            <Routes>
              {/* ===== Public routes (no authentication, no MasterLayout) ===== */}
              <Route path="/logincallback" element={<Callback />} />
              <Route path="/silent-renew" element={<SilentRenewCallback />} />
              <Route path="/logoutcallback" element={<LogoutCallback />} />
              <Route path="/signuptrial" element={<Navigate to="/signup" replace />} />
              <Route path="/signup-trial" element={<Navigate to="/signup" replace />} />
              <Route path="/signup" element={<SignupFree />} />
              <Route path="/privacypolicy" element={<PrivacyPolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/widget" element={<Widget />} />
              <Route path="/p/:slug" element={<ErrorBoundary><PublicProfileDisplay /></ErrorBoundary>} />

              {/* ===== Authenticated routes with MasterLayout ===== */}
              <Route element={<PrivateRoute />}>
                <Route element={<OnboardingGate />}>
                <Route element={<MasterLayout />}>
                  {/* Home / Dashboard */}
                  <Route path="/" element={<CompanyDashboard />} />
                  <Route path="/apps" element={<CompanyDashboard />} />

                  {/* Pending Actions — every user can have items (approvals + self-actions);
                      the aggregator scopes per caller, so no role guard here. */}
                  <Route path="/pending-actions" element={<PendingActions />} />

                  {/* AI Implementation Consultant — HR/Admin only (configures the system via chat) */}
                  <Route path="/ai-consultant" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><AIConsultant /></RoleProtectedRoute>} />
                  <Route path="/ai-consultant/history" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><AIConsultantHistory /></RoleProtectedRoute>} />

                  {/* Notifications */}
                  <Route path="/:company?/notifications" element={<Notifications />} />

                  {/* Attendance */}
                  {/* The leading `:company?` (vestigial — never read) collided with
                      `/brochure/attendance`, sending Free-tier users to the actual
                      Attendance page instead of the brochure. Restrict to `/attendance/...`. */}
                  <Route path="/attendance/:action?/:shiftId?" element={<Attendance />} />
                  <Route path="/attendance/overview/:targetUserId?/:targetUserFullName?" element={<AttendanceOverview />} />
                  <Route path="/attendance/:company?/leavesAndHolidays" element={<Navigate to="/my-leaves" replace />} />
                  <Route path="/attendance/:company?/maps" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><Maps /></RoleProtectedRoute>} />
                  <Route path="/attendance/:company?/requests" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <Navigate to="/leave-approvals" replace />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/attendance/correction-requests" element={<AttendanceCorrectionRequests />} />
                  <Route path="/attendance/colleagues-availability" element={<Dashboard />} />
                  <Route path="/attendance/dashboard" element={<Navigate to="/attendance/colleagues-availability" replace />} />
                  <Route path="/attendance/team-attendance" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><TeamAttendancePage /></RoleProtectedRoute>} />
                  <Route path="/attendance/work-schedules" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><WorkSchedules /></RoleProtectedRoute>} />
                  <Route path="/attendance/workschedules" element={<Navigate to="/attendance/work-schedules" replace />} />
                  <Route path="/attendance/work-schedules/recurring" element={<RoleProtectedRoute allowedRoles={MANAGER_ROLES}><RecurringSchedules /></RoleProtectedRoute>} />
                  <Route path="/attendance/workschedules/recurring" element={<Navigate to="/attendance/work-schedules/recurring" replace />} />
                  {/* Shift Swaps — any employee can request; the approvals tab self-gates by role inside the page.
                      Route is guarded so it's hidden when the feature isn't enabled for the user/company (UX only;
                      the backend remains the security boundary). */}
                  <Route path="/shift-swaps" element={<ShiftSwapEnabledRoute><ShiftSwaps /></ShiftSwapEnabledRoute>} />

                  {/* Attendance Reports */}
                  <Route path="/attendance/reports" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <Reports />
                    </RoleProtectedRoute>
                  } />
                  {/* Canonical kebab-case report routes */}
                  <Route path="/attendance/reports/users-statuses/:from/:to/:parameters/:format" element={<UsersStatusesReportExport />} />
                  <Route path="/attendance/reports/clockings/:from/:to/:parameters/:format" element={<ClockingsReportExport />} />
                  <Route path="/attendance/reports/shifts/:from/:to/:parameters/:format" element={<ShiftsReportExport />} />
                  <Route path="/attendance/reports/overtime/:from/:to/:parameters/:format" element={<OvertimeReportExport />} />
                  <Route path="/attendance/reports/clockings" element={<ClockingsReport />} />
                  <Route path="/attendance/reports/shifts" element={<ShiftsReport />} />
                  <Route path="/attendance/reports/work-sessions" element={<WorkSessionsReport />} />
                  <Route path="/attendance/reports/overtime" element={<OvertimeReport />} />
                  <Route path="/attendance/reports/out-of-bounds" element={<OutOfBoundsReport />} />
                  {/* Back-compat: old PascalCase report routes (Navigate can't interpolate multi-param URLs) */}
                  <Route path="/attendance/reports/UsersStatuses/:from/:to/:parameters/:format" element={<UsersStatusesReportExport />} />
                  <Route path="/attendance/reports/Clockings/:from/:to/:parameters/:format" element={<ClockingsReportExport />} />
                  <Route path="/attendance/reports/Shifts/:from/:to/:parameters/:format" element={<ShiftsReportExport />} />
                  <Route path="/attendance/reports/Overtime/:from/:to/:parameters/:format" element={<OvertimeReportExport />} />
                  <Route path="/attendance/reports/Clockings" element={<Navigate to="/attendance/reports/clockings" replace />} />
                  <Route path="/attendance/reports/Shifts" element={<Navigate to="/attendance/reports/shifts" replace />} />
                  <Route path="/attendance/reports/WorkSessions" element={<Navigate to="/attendance/reports/work-sessions" replace />} />
                  <Route path="/attendance/reports/Overtime" element={<Navigate to="/attendance/reports/overtime" replace />} />

                  {/* Users — Hr/Admin batch importer (company-scoped to their own company inside the component) */}
                  <Route path="/:company?/users/add-multiple" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><BatchAddingUsers /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/addmultiple" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><BatchAddingUsers /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/add" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><AddUser /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/send-notification" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><OnDemandNotification /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/sendNotification" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><OnDemandNotification /></RoleProtectedRoute>} />
                  <Route path="/:company?/users/:id/leavesAndHolidays" element={<Navigate to="/my-leaves" replace />} />

                  {/* Payroll — HR-only dashboards; claims/myclaims/payslips reachable by
                      employees for their own data and are scoped server-side. */}
                  <Route path="/payroll/table" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <UserPayroll />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/payroll/dashboard" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <DashboardPayroll />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/payroll/claims/:claimId" element={<ClaimDetails />} />
                  <Route path="/payroll/claims" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <Claims />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/payroll/my-claims" element={<MyClaims />} />
                  <Route path="/payroll/myclaims" element={<Navigate to="/payroll/my-claims" replace />} />
                  <Route path="/payroll/payslips" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <Payslips />
                    </RoleProtectedRoute>
                  } />
                  {(complianceCountry === 'UAE' || complianceCountry === 'KSA') && (
                    <Route path="/payroll/compliance-center" element={
                      <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                        <ComplianceCenter />
                      </RoleProtectedRoute>
                    } />
                  )}
                  {(complianceCountry === 'UAE' || complianceCountry === 'KSA') && (
                    <Route path="/payroll/wps-center" element={
                      <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                        <WpsCenter />
                      </RoleProtectedRoute>
                    } />
                  )}
                  {(complianceCountry === 'UAE' || complianceCountry === 'KSA') && (
                    <Route path="/payroll/pension" element={
                      <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                        <PensionCenter />
                      </RoleProtectedRoute>
                    } />
                  )}

                  {/* Employees Cycle */}
                  <Route path="/core-hr/directory" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><EmployeesGeneral /></RoleProtectedRoute>} />
                  {/* Hr/Admin welcome-emails management (fixed path; component pins to their own company via redux) */}
                  <Route path="/core-hr/welcome-emails" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><WelcomeEmails /></RoleProtectedRoute>} />
                  <Route path="/core-hr/org-structure" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><OrganizationalStructure /></RoleProtectedRoute>} />
                  <Route path="/core-hr/grouping" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><EmployeesGrouping /></RoleProtectedRoute>} />
                  <Route path="/core-hr/SetUp" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><SetUp /></RoleProtectedRoute>} />
                  <Route path="/core-hr/skills-overview" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><SkillsOverview /></RoleProtectedRoute>} />

                  {/* Profile routes - NOW INSIDE MasterLayout (sidebar + topbar persist) */}
                  <Route path="/profile/:id/personal" element={<Personal />} />
                  <Route path="/profile/:id/skills" element={<Skills />} />
                  <Route path="/profile/:id/payroll/active-payroll-month" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><Payroll /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/variable-pay" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><VariablePay /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/adjustments" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><Adjustments /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/benefits" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><UserBenefits /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/pay-history" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><PayHistory /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/salary-configuration" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><SalaryConfiguration /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/end-of-service" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><EndOfService /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/final-settlement" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><FinalSettlement /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/attendance/rules" element={<AttendanceRules />} />
                  <Route path="/profile/:id/leaves" element={<UserProfileLeaves />} />
                  <Route path="/profile/:id/map" element={<AttendanceMap />} />
                  {/* Documents also hosts the self-service "On Demand Documents" section (own profile only). */}
                  <Route path="/profile/:id/documents" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><Documents /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/assets" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><ProfileAssets /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/business-roles" element={<ProfileBusinessRoles />} />
                  {/* Back-compat: old camelCase profile sub-routes serve the same component (Navigate can't interpolate :id) */}
                  <Route path="/profile/:id/payroll/activePayrollMonth" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><Payroll /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/variablePay" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><VariablePay /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/payhistory" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><PayHistory /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/salaryconfiguration" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><SalaryConfiguration /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/endOfService" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><EndOfService /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/payroll/finalSettlement" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><FinalSettlement /></ProfileAccessGuard>} />
                  <Route path="/profile/:id/businessRoles" element={<ProfileBusinessRoles />} />
                  <Route path="/profile/:id/dependents" element={<ProfileDependents />} />
                  <Route path="/profile/:id/payroll/payslips" element={<ProfileAccessGuard othersAllowedRoles={HR_ONLY_ROLES}><UserProfilePayslips /></ProfileAccessGuard>} />

                  {/* Settings */}
                  <Route path="/settings/general-details" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><GeneralDetails /></RoleProtectedRoute>} />
                  <Route path="/settings/generalDetails" element={<Navigate to="/settings/general-details" replace />} />
                  <Route path="/settings/entities" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><Entities /></RoleProtectedRoute>} />
                  <Route path="/settings/currency-and-banks" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><Currency /></RoleProtectedRoute>} />
                  <Route path="/settings/currencyAndBanks" element={<Navigate to="/settings/currency-and-banks" replace />} />
                  <Route path="/settings/branches-and-departments" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><BranchesAndDepartments /></RoleProtectedRoute>} />
                  <Route path="/settings/branchesAndDepartments" element={<Navigate to="/settings/branches-and-departments" replace />} />
                  <Route path="/settings/payroll/gross-pays" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><GrossPaysConfig /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/grosspays" element={<Navigate to="/settings/payroll/gross-pays" replace />} />
                  <Route path="/settings/payroll/claims" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><ClaimsSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/variable-pays" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><CompanyVariablePayTypesSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/variablepays" element={<Navigate to="/settings/payroll/variable-pays" replace />} />
                  <Route path="/settings/payroll/benefits" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><CompanyBenefitTypesSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/end-of-service" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><EndOfServiceSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/payroll/endOfService" element={<Navigate to="/settings/payroll/end-of-service" replace />} />
                  <Route path="/settings/payroll/payslips" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><PayslipsSettings /></RoleProtectedRoute>} />
                  {(complianceCountry === 'UAE' || complianceCountry === 'KSA' || props.role === 'Operator') && (
                    <Route path="/settings/payroll/compliance" element={
                      <RoleProtectedRoute allowedRoles={HR_AND_OPERATOR_ROLES}>
                        <ComplianceSettings />
                      </RoleProtectedRoute>
                    } />
                  )}
                  <Route path="/settings/company/terms-and-policies" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <TermsAndPoliciesSetup />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/onboarding/complete" element={<MyOnboardingPage />} />
                  <Route path="/terms-and-policies/accept" element={<TermsAcceptanceView />} />
                  {/* Exit/termination reasons are managed under Lifecycle Settings → Exit Reasons tab. Old dead mock page removed; redirect legacy paths there. */}
                  <Route path="/settings/termination-reasons" element={<Navigate to="/settings/lifecycle" replace />} />
                  <Route path="/settings/reasonsOfTerminationResignation" element={<Navigate to="/settings/lifecycle" replace />} />
                  <Route path="/settings/lifecycle" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><LifecycleSettings /></RoleProtectedRoute>} />
                  <Route path="/settings/core-hr/asset-management" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><AssetManagementSettings /></RoleProtectedRoute>} />
                  {/* Attendance Rules merged into Work Schedule Config (single company-rules page). */}
                  <Route path="/settings/attendance/attendance-rules" element={<Navigate to="/settings/attendance/work-schedule-config" replace />} />
                  <Route path="/settings/attendance/rulesManagement" element={<Navigate to="/settings/attendance/work-schedule-config" replace />} />
                  <Route path="/settings/attendance/leavesManagement" element={<Navigate to="/leave-policies" replace />} />
                  <Route path="/settings/attendance/holidays" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><CompanyHolidays /></RoleProtectedRoute>} />
                  <Route path="/settings/attendance/holidaysManagement" element={<Navigate to="/settings/attendance/holidays" replace />} />
                  <Route path="/settings/attendance/workplace-map" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><WorkplaceMap /></RoleProtectedRoute>} />
                  <Route path="/settings/attendance/workplaceMap" element={<Navigate to="/settings/attendance/workplace-map" replace />} />
                  <Route path="/settings/attendance/work-schedule-config" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><Weekschedule /></RoleProtectedRoute>} />
                  <Route path="/settings/attendance/workscheduleconfig" element={<Navigate to="/settings/attendance/work-schedule-config" replace />} />
                  <Route path="/settings/attendance/face-recognition" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <FaceRecognitionSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/attendance/wialon" element={
                    <RoleProtectedRoute allowedRoles={['Operator']}>
                      <WialonSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/general/file-upload-settings" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <FileUploadSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/general/ocr-settings" element={
                    <RoleProtectedRoute allowedRoles={SETTINGS_ROLES}>
                      <OcrSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/blob-migration" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <BlobMigration />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/project-management/projects" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><Projects /></RoleProtectedRoute>} />
                  <Route path="/settings/projectmanagement/projects" element={<Navigate to="/settings/project-management/projects" replace />} />
                  <Route path="/settings/companies/add" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><AddEditCompany /></RoleProtectedRoute>} />
                  <Route path="/settings/companies/:id" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><AddEditCompany /></RoleProtectedRoute>} />
                  <Route path="/settings/companies/:id/users" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><CompanyUsers /></RoleProtectedRoute>} />
                  <Route path="/settings/companies/:id/batch-add-users" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><BatchAddingUsers /></RoleProtectedRoute>} />
                  <Route path="/settings/companies/:id/welcome-emails" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><WelcomeEmails /></RoleProtectedRoute>} />
                  {/* Old camelCase URL kept active alongside new path (Navigate can't interpolate :id) */}
                  <Route path="/settings/companies/:id/batchAddingUsers" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><BatchAddingUsers /></RoleProtectedRoute>} />
                  <Route path="/settings/companies/:companyId/leavesManagement" element={<Navigate to="/leave-policies" replace />} />
                  <Route path="/settings/companies" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><Companies /></RoleProtectedRoute>} />
                  <Route path="/settings/operator/company-rules" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <OperatorCompanyRules />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/operator/subscriptions" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <SubscriptionsIndex />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/operator/platform-terms" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <PlatformTermsIndex />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/operator/app-version" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <AppVersionIndex />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/start-trial" element={
                    <RoleProtectedRoute allowedRoles={['Hr', 'Admin']}>
                      <StartTrial />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/organizations/add" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><AddOrganization /></RoleProtectedRoute>} />
                  <Route path="/settings/organizations" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><Organizations /></RoleProtectedRoute>} />
                  <Route path="/settings/general/attachment-types" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><AttachmentTypes /></RoleProtectedRoute>} />
                  <Route path="/settings/general/attachmentTypes" element={<Navigate to="/settings/general/attachment-types" replace />} />
                  <Route path="/settings/general/date-formats" element={
                    <RoleProtectedRoute allowedRoles={OPERATOR_ROLES}>
                      <DateFormatConfiguration />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/general/dateFormats" element={<Navigate to="/settings/general/date-formats" replace />} />
                  <Route path="/settings/system-emails" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><SystemEmails /></RoleProtectedRoute>} />
                  <Route path="/settings/systemEmails" element={<Navigate to="/settings/system-emails" replace />} />
                  <Route path="/settings/development/seeding" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><Seeding /></RoleProtectedRoute>} />
                  <Route path="/settings/ai-settings" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><AISettings /></RoleProtectedRoute>} />
                  <Route path="/settings/notifications" element={<UserNotificationPreferences />} />
                  <Route path="/settings/notification-management" element={
                    <RoleProtectedRoute allowedRoles={SETTINGS_ROLES}>
                      <AdminNotificationManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/module-config" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><ModuleConfig /></RoleProtectedRoute>} />
                  <Route path="/settings/moduleConfig" element={<Navigate to="/settings/module-config" replace />} />
                  <Route path="/settings/employee-impact/recognition-privacy" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><RecognitionPrivacySettings /></RoleProtectedRoute>} />
                  <Route path="/settings/employee-impact/recognition-values" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><RecognitionValues /></RoleProtectedRoute>} />
                  <Route path="/settings/employeeImpact/privacySettings" element={<Navigate to="/settings/employee-impact/recognition-privacy" replace />} />
                  <Route path="/settings/employeeImpact/recognitionValues" element={<Navigate to="/settings/employee-impact/recognition-values" replace />} />
                  <Route path="/organizations/:id/companies" element={<RoleProtectedRoute allowedRoles={OPERATOR_ROLES}><Companies /></RoleProtectedRoute>} />

                  {/* Tournament Management — canonical kebab-case routes */}
                  <Route path="/tournament-management/tournaments" element={<TournamentList />} />
                  <Route path="/tournament-management/matches" element={<TournamentMatches />} />
                  <Route path="/tournament-management/referee-assignment" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><RefereeAssignmentCenter /></RoleProtectedRoute>} />
                  <Route path="/tournament-management/my-assignments" element={<MyAssignments />} />
                  <Route path="/tournament-management/work-points" element={
                    <RoleProtectedRoute allowedRoles={['Hr', 'Admin']}>
                      <WorkPointSettings />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/tournament-management/referees" element={
                    <RoleProtectedRoute allowedRoles={['Hr', 'Admin']}>
                      <RefereeManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/tournament-management/compensation" element={
                    <RoleProtectedRoute allowedRoles={['Hr', 'Admin']}>
                      <Compensation />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/tournament-management/referee-workload" element={
                    <RoleProtectedRoute allowedRoles={['Hr', 'Admin']}>
                      <RefereeWorkload />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/tournament-management/marketplace" element={<MarketplaceHub />} />

                  {/* Back-compat redirects for old camelCase tournament URLs (preserve persisted notifications) */}
                  <Route path="/tournamentManagement/tournaments" element={<Navigate to="/tournament-management/tournaments" replace />} />
                  <Route path="/tournamentManagement/matches" element={<Navigate to="/tournament-management/matches" replace />} />
                  <Route path="/tournamentManagement/referee-assignment" element={<Navigate to="/tournament-management/referee-assignment" replace />} />
                  <Route path="/tournamentManagement/my-assignments" element={<Navigate to="/tournament-management/my-assignments" replace />} />
                  <Route path="/tournamentManagement/work-points" element={<Navigate to="/tournament-management/work-points" replace />} />
                  <Route path="/tournamentManagement/referees" element={<Navigate to="/tournament-management/referees" replace />} />
                  <Route path="/tournamentManagement/compensation" element={<Navigate to="/tournament-management/compensation" replace />} />
                  <Route path="/tournamentManagement/referee-workload" element={<Navigate to="/tournament-management/referee-workload" replace />} />
                  <Route path="/tournamentManagement/marketplace" element={<Navigate to="/tournament-management/marketplace" replace />} />

                  {/* Employee Impact — canonical kebab-case routes */}
                  <Route path="/employee-impact/performance-dashboard" element={<ImpactDashboard />} />
                  <Route path="/employee-impact/engagement-dashboard" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <InsightsDashboard />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/employee-impact/recognition-and-feedback" element={<Recognition />} />
                  <Route path="/employee-impact/goals" element={<Goals />} />
                  <Route path="/employee-impact/enps" element={<ESPN />} />
                  <Route path="/employee-impact/reflections" element={<Reflections />} />
                  <Route path="/employee-impact/team-hub" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <TeamHub />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/employee-impact/team-reflections" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <TeamReflections />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/employee-impact/mission-statements" element={<MissionStatements />} />
                  <Route path="/employee-impact/mentorship" element={<Mentorship />} />
                  <Route path="/ai-impact-analysis" element={<AIImpactDashboard />} />

                  {/* Back-compat redirects for renamed Employee Impact routes */}
                  <Route path="/employeeImpact/performanceDashboard" element={<Navigate to="/employee-impact/performance-dashboard" replace />} />
                  <Route path="/employeeImpact/impactDashboard" element={<Navigate to="/employee-impact/performance-dashboard" replace />} />
                  <Route path="/employeeImpact/insights" element={<Navigate to="/employee-impact/engagement-dashboard" replace />} />
                  <Route path="/employeeImpact/recognitionOrFeedback" element={<Navigate to="/employee-impact/recognition-and-feedback" replace />} />
                  <Route path="/employeeImpact/goals" element={<Navigate to="/employee-impact/goals" replace />} />
                  <Route path="/employeeImpact/enps" element={<Navigate to="/employee-impact/enps" replace />} />
                  <Route path="/employeeImpact/reflections" element={<Navigate to="/employee-impact/reflections" replace />} />
                  <Route path="/employeeImpact/teamHub" element={<Navigate to="/employee-impact/team-hub" replace />} />
                  <Route path="/employeeImpact/TeamReflections" element={<Navigate to="/employee-impact/team-reflections" replace />} />
                  <Route path="/employeeImpact/mission-statements" element={<Navigate to="/employee-impact/mission-statements" replace />} />
                  <Route path="/employeeImpact/mentorship" element={<Navigate to="/employee-impact/mentorship" replace />} />
                  <Route path="/ai-impact-dashboard" element={<Navigate to="/ai-impact-analysis" replace />} />

                  {/* Coming Soon - Rich Preview Features */}
                  <Route path="/core-hr/dependents-directory" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <DependentsDirectory />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/core-hr/team-role-management" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><TeamRoleManagement /></RoleProtectedRoute>} />

                  {/* Coming Soon Routes - Rich Preview Pages */}
                  <Route path="/employee-document-center" element={<EmployeeDocumentCenter />} />
                  <Route path="/employment-lifecycle" element={<RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}><EmploymentLifecycle /></RoleProtectedRoute>} />
                  <Route path="/departments-locations" element={<RoleProtectedRoute allowedRoles={CORE_HR_ROLES}><DepartmentsLocations /></RoleProtectedRoute>} />
                  <Route path="/job-titles-grades" element={<JobTitlesGrades />} />
                  <Route path="/company-files-hr-policies" element={<CompanyFilesPolicies />} />
                  <Route path="/profile-authorization-settings" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><ProfileAuthorization /></RoleProtectedRoute>} />
                  <Route path="/document-template-manager" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><DocumentTemplateManager /></RoleProtectedRoute>} />
                  {/* team-role-management-dashboard redirects to new path */}
                  <Route path="/hr-process-automation" element={<HRProcessAutomation />} />
                  <Route path="/hr-process-automation-center" element={<Navigate to="/hr-process-automation" replace />} />
                  <Route path="/core-hr/reports" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><CoreHRReports /></RoleProtectedRoute>} />
                  <Route path="/attendance-dashboard" element={<AttendanceDashboard />} />
                  <Route path="/duty-roster-management" element={<DutyRosterManagement />} />
                  <Route path="/payroll-run-wizard" element={<PayrollRunWizard />} />
                  <Route path="/payroll-documents-center" element={<PayrollDocuments />} />
                  <Route path="/payroll-reports" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><PayrollReports /></RoleProtectedRoute>} />
                  {complianceCountry === 'UAE' && (
                    <Route path="/eos-center" element={<EOSCenter />} />
                  )}
                  <Route path="/employee-development-hub" element={<EmployeeDevelopmentHub />} />
                  <Route path="/development-objectives-creator" element={<DevelopmentObjectivesCreator />} />
                  <Route path="/development-objective-detail" element={<DevelopmentObjectiveDetail />} />
                  <Route path="/team-development-dashboard" element={<TeamDevelopmentDashboard />} />
                  <Route path="/manager-development-objectives-creator" element={<Navigate to="/development-objectives-creator" replace />} />
                  <Route path="/development-objective-detail-center" element={<Navigate to="/development-objective-detail" replace />} />
                  <Route path="/manager-team-development-dashboard" element={<Navigate to="/team-development-dashboard" replace />} />
                  <Route path="/check-ins-hub" element={<CheckInsHub />} />
                  <Route path="/performance-snapshot" element={<RoleProtectedRoute allowedRoles={HR_AND_ADMIN_ROLES}><PerformanceSnapshot /></RoleProtectedRoute>} />
                  <Route path="/behavior-and-values" element={<BehaviorValues />} />
                  <Route path="/behavior-values-layer" element={<Navigate to="/behavior-and-values" replace />} />
                  <Route path="/settings/core-hr-settings" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><CoreHRSettings /></RoleProtectedRoute>} />
                  <Route path="/task-board" element={<TaskBoard />} />
                  <Route path="/projects-list" element={<ProjectsList />} />
                  <Route path="/core-hr/bulk-document-upload" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <BulkUploadPage />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/core-hr/assets-management" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <AssetsPage />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/hr-enps-analytics" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <HRENPSAnalyticsDashboard />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/my-leaves" element={<MyLeaves />} />
                  <Route path="/leave-approvals" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <LeaveApprovals />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/delegations" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <DelegationManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/team-calendar" element={
                    <RoleProtectedRoute allowedRoles={MANAGER_ROLES}>
                      <TeamCalendar />
                    </RoleProtectedRoute>
                  } />
                  {/* Re-enabled as the Pending Actions deep-link target for return-to-work items. */}
                  <Route path="/return-to-work" element={
                    <ReturnToWork />
                  } />
                  {/* <Route path="/comp-off" element={
                    <CompOffCredits />
                  } /> */}
                  {/* <Route path="/short-leave" element={
                    <ShortLeave />
                  } /> */}
                  {/* <Route path="/encashment" element={
                    <Encashment />
                  } /> */}
                  {/* <Route path="/recurring-leave" element={
                    <RecurringLeave />
                  } /> */}
                  <Route path="/restricted-holidays" element={
                    <Navigate to="/leave-policies" replace />
                  } />
                  <Route path="/statutory-leave-templates" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <StatutoryLeaveTemplates />
                    </RoleProtectedRoute>
                  } />
                  {/* <Route path="/year-end-processing" element={
                    <YearEndProcessing />
                  } /> */}
                  <Route path="/leave-statements" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <LeaveStatements />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/leave-balances" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <LeaveBalances />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/absence-analytics" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <AbsenceAnalytics />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/attendance/leave-policies" element={
                    <Navigate to="/leave-policies" replace />
                  } />
                  <Route path="/leave-policies" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <LeaveSettings />
                    </RoleProtectedRoute>
                  } />

                  {/* Recruitment (UI only — ported from rocketnew) */}
                  <Route path="/recruitment-dashboard" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <RecruitmentDashboard />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/job-openings" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <JobOpeningsManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/candidates" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <CandidatesManagementCenter />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/pipeline" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <HiringPipeline />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/interviews" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <InterviewsManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/recruitment/offers" element={
                    <RoleProtectedRoute allowedRoles={[...HR_ONLY_ROLES, 'Admin']}>
                      <OffersManagement />
                    </RoleProtectedRoute>
                  } />
                  <Route path="/settings/recruitment" element={
                    <RoleProtectedRoute allowedRoles={HR_ONLY_ROLES}>
                      <RecruitmentSettings />
                    </RoleProtectedRoute>
                  } />
                  {/* Aliases used by deep-links inside the rocketnew-style pages */}
                  <Route path="/job-openings-management" element={<Navigate to="/recruitment/job-openings" replace />} />
                  <Route path="/candidates-management-center" element={<Navigate to="/recruitment/candidates" replace />} />
                  <Route path="/hiring-pipeline" element={<Navigate to="/recruitment/pipeline" replace />} />
                  <Route path="/interviews-management" element={<Navigate to="/recruitment/interviews" replace />} />
                  <Route path="/offers-management" element={<Navigate to="/recruitment/offers" replace />} />
                  <Route path="/recruitment-settings" element={<Navigate to="/settings/recruitment" replace />} />

                  {/* Module landing redirects.
                      These prefixes appear in `data/lockedRoutes.ts` and in the
                      sidebar's "expand to first child" behavior — but the bare
                      URLs (e.g. /recruitment) never had their own route, so
                      direct navigation rendered a blank page. Redirect each to
                      the canonical first sub-route. Free users still see the
                      LockedShell wrapping the sub-route via MasterLayout. */}
                  <Route path="/recruitment" element={<Navigate to="/recruitment/job-openings" replace />} />
                  <Route path="/payroll" element={<Navigate to="/payroll/dashboard" replace />} />
                  <Route path="/employee-impact" element={<Navigate to="/employee-impact/recognition-and-feedback" replace />} />
                  <Route path="/tournament-management" element={<Navigate to="/tournament-management/tournaments" replace />} />

                  {/* Per-module brochures — Phase 6. Free-tier users hitting a
                      locked module get pointed here from LockedShell + the
                      BrochureOverlay. The router renders the right brochure
                      (Attendance, Leaves, etc.) and falls back to a "coming
                      soon" card for slugs still in flight. */}
                  <Route path="/brochure/:moduleSlug" element={<BrochureRouter />} />

                  {/* Public Profile Pages */}
                  <Route path="/profile-settings" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><ProfileSettings /></RoleProtectedRoute>} />
                  <Route path="/profile-configuration-center" element={<ProfileConfigurationCenter />} />
                  <Route path="/profile-configuration-center/:userId" element={<ProfileConfigurationCenter />} />
                  <Route path="/hr-admin-public-profile-settings" element={<RoleProtectedRoute allowedRoles={SETTINGS_ROLES}><HRAdminPublicProfileSettings /></RoleProtectedRoute>} />
                  <Route path="/public-profile-display" element={<ErrorBoundary><PublicProfileDisplay /></ErrorBoundary>} />
                </Route>
                </Route>
              </Route>
            </Routes>
            </div>

            {/* AI Assistant - Always available on the side when logged in */}
            {isLoggedIn && <AIAssistant />}

            {/* OTP Toast - Global listener for real-time OTP delivery via SignalR */}
            {isLoggedIn && <OtpToast />}

            {/* Notification Popup - Global listener for real-time notification delivery via SignalR */}
            {isLoggedIn && <NotificationPopup />}

          </I18nextProvider>
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    isLoggedIn: state.user.isLoggedIn,
    fullName: state.user.fullName,
    role: state.user.role,
    locale: state.user.locale,
    companyName: state.user.companyName,
    companyLogo: state.user.companyLogo,
    companyLocales: state.user.companyLocales,
    companyPrimaryColor: state.user.companyPrimaryColor,
    currentStatus: state.user.currentStatus,
    usercurrentCustomStatusText: state.user.usercurrentCustomStatusText,
    prevUrl: state.user.prevUrl,
    userId: state.user.userId,
    imageUrl: state.user.imageUrl,
    anyUserWithVariableScheduleRule: state.user.anyUserWithVariableScheduleRule,
    token: state.user.token,
    isManager: state.user.isManager,
    rtl: state.user.rtl,
  };
}

const App = connect(mapStateToProps)(_App);

export default function () {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}
